import React from 'react';

// Ant Design
import { Table, Tag, Tooltip, Progress } from 'antd';
import moment from 'moment';

export const getStatus = ({ isArchived, isPushedToS3MJ, isPushedToS3Statara, isPublished, jobStatus, prePublishedMetadata, audienceId }) => {
  // Live job status takes priority
  if (jobStatus) {
    const { jobType, status, progress, message } = jobStatus;
    if (status === 'ACTIVE') {
      if (jobType === 'SAVE') return { label: `Saving... ${progress}%`, color: 'blue', progress };
      if (jobType === 'ACTIVATE') return { label: `Activating... ${progress}%`, color: 'orange', progress };
      if (jobType === 'PUBLISH') return { label: `Publishing... ${progress}%`, color: 'orange', progress };
    }
    if (status === 'WAITING') return { label: 'Queued', color: 'blue', progress: 0 };
    if (status === 'COMPLETED') {
      if (jobType === 'SAVE') return { label: 'Ready to Activate', color: 'gold', progress: null };
      if (jobType === 'ACTIVATE') return { label: 'Ready to Publish', color: 'gold', progress: null };
      if (jobType === 'PUBLISH') return { label: 'Published', color: 'green', progress: null };
    }
    if (status === 'FAILED') return { label: 'Failed', color: 'red', progress: 0, errorMessage: message };
  }

  // Check for persisted active job state (survives page refresh)
  // Consider stale if job started more than 30 minutes ago without progress updates
  if (prePublishedMetadata?.status === 'ACTIVE') {
    const startedAt = prePublishedMetadata.startedAt ? new Date(prePublishedMetadata.startedAt) : null;
    const isStale = startedAt && (Date.now() - startedAt.getTime() > 30 * 60 * 1000);

    if (!isStale) {
      const { jobType, progress, message } = prePublishedMetadata;
      if (jobType === 'SAVE') return { label: `Saving... ${progress || 0}%`, color: 'blue', progress: progress || 0 };
      if (jobType === 'ACTIVATE') return { label: `Activating... ${progress || 0}%`, color: 'orange', progress: progress || 0 };
      if (jobType === 'PUBLISH') return { label: `Publishing... ${progress || 0}%`, color: 'orange', progress: progress || 0 };
    }
    // If stale, fall through to check boolean flags
  }

  // Check for persisted job error
  if (prePublishedMetadata?.error) {
    return { label: 'Failed', color: 'red', progress: 0, errorMessage: prePublishedMetadata.error };
  }

  // Fall back to persisted boolean flags
  if (isArchived) return { label: 'Archived', color: 'red', progress: null };
  if (isPublished) return { label: 'Published', color: 'green', progress: null };
  if (isPushedToS3Statara && !isPublished) return { label: 'Ready to Publish', color: 'gold', progress: null };
  if (isPushedToS3MJ) return { label: 'Ready to Activate', color: 'gold', progress: null };
  if (audienceId && !isPushedToS3MJ) return { label: 'Saving...', color: 'blue', progress: null };
  return { label: 'Draft', color: 'default', progress: null };
};

export const demographicsAudienceTableColumnMobile = [
  {
    title: 'Audience Name',
    dataIndex: 'name',
    key: 'name',
    render: (_, record) => {
      return (
        <div
          style={{
            border: '1px solid black',
            padding: '20px',
            borderRadius: '20px',
            borderLeft: '5px solid #1677FF',
          }}
        >
          <p style={{ marginBottom: '0.1em' }}>
            {record.name} {record.isArchived && <Tag color="red">Archived</Tag>}
          </p>
          <p style={{ marginBottom: '0.1em' }}>
            <b>Created On</b> {moment(record.createdAt).format('MMM D, YYYY')}
          </p>
          <p style={{ marginBottom: '0.1em' }}>
            <b>Reach</b> {record && record.reachCount ? record.reachCount.toLocaleString() : 0}
          </p>
        </div>
      );
    },
  },
];

export const demographicsAudienceTableColumn = [
  Table.EXPAND_COLUMN,
  {
    title: 'Audience Name',
    dataIndex: 'name',
    key: 'name',
    width: '25%',
    ellipsis: true,
    sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    render: (text) => <span>{text}</span>,
    onCell: () => ({ style: { cursor: 'default' } }),
  },
  {
    title: 'Reach',
    dataIndex: 'reachCount',
    key: 'reachCount',
    width: '15%',
    sorter: (a, b) => (a.reachCount || 0) - (b.reachCount || 0),
    render: (reachCount) => {
      return <span>{reachCount ? reachCount.toLocaleString() : 0}</span>;
    },
    onCell: () => ({ style: { cursor: 'default' } }),
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: '18%',
    sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    defaultSortOrder: 'descend',
    render: (createdAt) => moment(createdAt).format('M/D/YYYY h:mma'),
    onCell: () => ({ style: { cursor: 'default' } }),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: '25%',
    render: (_, record) => {
      const parsedMetadata = (() => {
        try {
          const meta = typeof record.prePublishedMetadata === 'string'
            ? JSON.parse(record.prePublishedMetadata)
            : record.prePublishedMetadata;
          // Handle legacy { set: ... } wrapper from Prisma
          return meta?.set || meta;
        } catch { return null; }
      })();

      const statusInfo = getStatus({
        isArchived: record.isArchived,
        isPushedToS3MJ: record.isPushedToS3MJ,
        isPushedToS3Statara: record.isPushedToS3Statara,
        isPublished: record.isPublished,
        jobStatus: record._jobStatus,
        prePublishedMetadata: parsedMetadata,
        audienceId: record.audienceId,
      });

      return (
        <div>
          <Tooltip title={statusInfo.errorMessage || statusInfo.label}>
            <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
          </Tooltip>
          {statusInfo.progress !== null && statusInfo.progress !== undefined && (
            <Progress
              percent={statusInfo.progress}
              size="small"
              showInfo={false}
              strokeColor={statusInfo.color === 'blue' ? '#1890ff' : '#fa8c16'}
              style={{ marginTop: 4, width: '90%' }}
            />
          )}
        </div>
      );
    },
    onCell: () => ({ style: { cursor: 'default' } }),
  },
];
